/**
 * Cómo se reparte una lista de partidas entre los comensales de la mesa.
 *
 * Vive aparte porque la usan el papel de cocina, el ticket térmico y el ticket
 * en pantalla, y la regla del compartido no se puede reimplementar sin que una
 * de las copias se equivoque. El backend tiene su gemelo en
 * `src/helpers/comensales.js` para el recibo por correo — son repos distintos
 * sin paquete común; si cambia aquí, cambia allá.
 */

/**
 * Reparte las partidas en bloques por comensal.
 *
 * Sin comensales capturados devuelve un solo bloque sin encabezado, o sea la
 * lista de siempre: un mostrador no debe empezar a ver títulos que no le dicen
 * nada.
 *
 * Lo compartido NO se repite en el bloque de cada quien: se prepara y se cobra
 * UNA vez, y un renglón "2x CERVEZA" impreso dos veces son cuatro cervezas
 * servidas. Va en su propio bloque diciendo entre quiénes.
 *
 * Las etiquetas salen en minúsculas: cómo se presentan es de cada quien —la
 * impresora térmica las pasa por `strip()`, que las sube a mayúsculas.
 */
export function agruparPorComensal(items, comensales = []) {
  // Cómo se llama cada quien en el papel: su apodo si el mesero se lo puso, y
  // si no el número de siempre. "Sra" o "Niño" le sirven a la cocina para
  // emplatar y al cliente para entender su ticket; "Comensal 6" no.
  const apodoDe = (n) => comensales.find((c) => Number(c?.n) === Number(n))?.nombre?.trim() || '';
  const nombre  = (n) => apodoDe(n) || `Comensal ${n}`;
  // En "Comparten 1 y 2" el número solo basta: el encabezado ya dice de qué se
  // habla, y repetir "Comensal" en cada uno alarga una línea que en doble ancho
  // tiene 16 caracteres.
  const corto   = (n) => apodoDe(n) || `${n}`;
  // Se normaliza el orden aquí y no se confía en quien llama: [2,1] y [1,2] son
  // los mismos dos comensales y deben caer en el mismo bloque.
  const conSeats = items.map((i) => ({
    ...i,
    seats: [...new Set(i.seats ?? [])].sort((a, b) => a - b),
  }));
  const conDuenio = conSeats.filter((i) => i.seats.length);
  if (!conDuenio.length) return [{ etiqueta: null, items }];

  const solos = conDuenio.filter((i) => i.seats.length === 1);
  const enMesa = [...new Set(solos.map((i) => i.seats[0]))].sort((a, b) => a - b);

  const bloques = enMesa.map((n) => ({
    etiqueta: nombre(n),
    items: solos.filter((i) => i.seats[0] === n),
  }));

  // Compartidos: uno por cada conjunto distinto de comensales, para que la
  // botella de los cuatro y el postre de dos no se mezclen en un mismo bloque.
  const compartidos = conDuenio.filter((i) => i.seats.length > 1);
  const conjuntos = [...new Set(compartidos.map((i) => i.seats.join(',')))];
  for (const clave of conjuntos) {
    bloques.push({
      etiqueta: 'Comparten ' + listaLegible(clave.split(',').map(corto)),
      items: compartidos.filter((i) => i.seats.join(',') === clave),
    });
  }

  const deLaMesa = conSeats.filter((i) => !i.seats.length);
  if (deLaMesa.length) bloques.push({ etiqueta: 'Para la mesa', items: deLaMesa });

  return bloques;
}

// [1,2] -> "1 y 2" · [1,2,4] -> "1, 2 y 4"
function listaLegible(ns) {
  if (ns.length <= 1) return ns.join('');
  return ns.slice(0, -1).join(', ') + ' y ' + ns[ns.length - 1];
}

/**
 * Los mismos bloques, pero para la pantalla donde el mesero captura.
 *
 * Se parece a `agruparPorComensal` y no es la misma función porque el papel y
 * la captura quieren cosas distintas: un ticket con un bloque "Comensal 3" sin
 * nada abajo es un ticket mal impreso, mientras que en la comanda ESE es el
 * bloque más importante de todos —el del que todavía no ha pedido—. Antes no
 * existía, y por eso un comensal al que no se le había apuntado nada no tenía
 * dónde aparecer ni cómo volver a él.
 *
 * `comensales` son los que están sentados. Devuelve `null` cuando la mesa no
 * tiene a nadie separado: entonces el carrito es la lista de siempre y no hay
 * por qué ponerle títulos.
 */
export function agruparParaCaptura(items, comensales = []) {
  const conSeats = items.map((i) => ({
    ...i,
    seats: [...new Set(i.seats ?? [])].sort((a, b) => a - b),
  }));

  const nums = [...new Set([
    ...comensales.map((c) => c.n).filter(Number.isInteger),
    ...conSeats.flatMap((i) => i.seats),
  ])].sort((a, b) => a - b);

  if (!nums.length) return null;

  const bloques = nums.map((n) => ({
    clave: `c${n}`,
    seat:  n,
    items: conSeats.filter((i) => i.seats.length === 1 && i.seats[0] === n),
  }));

  // Un bloque por cada conjunto distinto: la botella de los cuatro y el postre
  // de dos no se mezclan.
  const compartidos = conSeats.filter((i) => i.seats.length > 1);
  for (const clave of [...new Set(compartidos.map((i) => i.seats.join(',')))]) {
    bloques.push({
      clave:  `s${clave}`,
      seats:  clave.split(',').map(Number),
      items:  compartidos.filter((i) => i.seats.join(',') === clave),
    });
  }

  const deLaMesa = conSeats.filter((i) => !i.seats.length);
  if (deLaMesa.length) bloques.push({ clave: 'mesa', seat: null, mesa: true, items: deLaMesa });

  return bloques;
}
